import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { PORTAL, usePortal } from '../portalAuth';
import SITE from '../api';
import StorePhoneInput from '../components/StorePhoneInput';
import Captcha from '../components/Captcha';
import { trackEvent } from '../analytics';
import { STORE_BASE, STORE_HOME } from '../routeBase';

// customer_type CHECK constraint values (models/database.js) — same
// vocabulary the admin's own Customers.js dropdown uses, just phrased for a
// storefront visitor instead of staff. This is the single strongest signal
// for telling a real wholesale/retail buyer apart from a stray consumer
// signup or spam — everything else on this form is secondary to it.
const BUSINESS_TYPES = [
  { value: 'distributor', label: 'Distributor / Wholesaler' },
  { value: 'retailer', label: 'Retailer / Optical Shop' },
  { value: 'hospital', label: 'Hospital / Eye Clinic' },
  { value: 'institution', label: 'Institution' },
  { value: 'other', label: 'Other' },
];

export default function Register() {
  const { login } = usePortal();
  const nav = useNavigate();
  const [countries, setCountries] = useState([]);
  const [f, setF] = useState({
    company_name: '', contact_person: '', customer_type: '', mobile: '', email: '',
    address: '', city: '', state: '', postal_code: '', country_id: '', gstin: '', notes: '', password: '',
  });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  // Item 4 — a new registrant used to get logged in and dropped straight
  // onto the home page with no indication that wholesale pricing needs
  // admin approval first, so the very next thing they saw was every price
  // missing with no explanation. This holds them on a short confirmation
  // screen instead, then sends them to the home page itself once it's read.
  const [justRegistered, setJustRegistered] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(8);
  const set = k => e => setF({ ...f, [k]: e.target.value });

  useEffect(() => { SITE.get('/countries').then(r => setCountries(r.data.countries || [])); }, []);

  useEffect(() => {
    if (!justRegistered) return;
    if (secondsLeft <= 0) { nav(STORE_HOME); return; }
    const t = setTimeout(() => setSecondsLeft(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [justRegistered, secondsLeft, nav]);

  const submit = async e => {
    e.preventDefault(); setErr('');
    if (!f.company_name || !f.mobile || !f.password) return setErr('Company name, mobile and password are required');
    if (!f.email || !f.email.trim()) return setErr('Email is required');
    if (!f.customer_type) return setErr('Please select your business type');
    if (f.password.length < 6) return setErr('Password must be at least 6 characters');
    setBusy(true);
    try {
      const r = await PORTAL.post('/register', { ...f, captcha_token: captchaToken });
      login(r.data.token, r.data.customer);
      setJustRegistered(true);
      trackEvent('account_registration', { customer_type: f.customer_type, method: 'website' });
    } catch (e2) { setErr(e2.response?.data?.error || 'Registration failed'); }
    setBusy(false);
  };

  if (justRegistered) {
    return (
      <div style={{ maxWidth: 560, margin: '60px auto', padding: '0 20px' }}>
        <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: '40px 36px', textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px' }}>
            <i className="ti ti-circle-check" style={{ fontSize: 30, color: '#16a34a' }} />
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 12 }}>Registration Successful</h1>
          <p style={{ fontSize: 14.5, color: '#444', lineHeight: 1.8, marginBottom: 14 }}>
            Thank you for registering with Salvador Eyewear. Your account has been created successfully.
          </p>
          <p style={{ fontSize: 14.5, color: '#444', lineHeight: 1.8, marginBottom: 14 }}>
            As this is a B2B wholesale platform, access to our pricing requires a brief verification by our team.
            Your account is now pending approval — once approved, wholesale pricing will be visible across the site automatically.
          </p>
          <p style={{ fontSize: 14.5, color: '#444', lineHeight: 1.8, marginBottom: 22 }}>
            We appreciate your patience. In the meantime, please feel free to browse our full product catalogue.
          </p>
          <button onClick={() => nav(STORE_HOME)} className="sf-btn-outline"
            style={{ padding: '10px 26px', border: '1.5px solid var(--sf-accent)', borderRadius: 22, fontSize: 13.5, fontWeight: 600, cursor: 'pointer', background: 'transparent' }}>
            Continue to homepage
          </button>
          <p style={{ fontSize: 12, color: '#aaa', marginTop: 16 }}>Redirecting automatically in {secondsLeft}s…</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 620, margin: '40px auto', padding: '0 20px' }}>
      <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: 32 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Create your account</h1>
        <p style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>Register to browse and order. Wholesale prices are enabled once our team reviews and approves your account.</p>
        {err && <div style={{ background: '#fef2f2', color: '#b91c1c', fontSize: 13, padding: '9px 12px', borderRadius: 8, marginBottom: 14 }}>{err}</div>}
        <form onSubmit={submit}>
          <Section title="Business Details">
            <Row>
              <F label="Company / Shop name *" value={f.company_name} onChange={set('company_name')} />
              <F label="Contact person" value={f.contact_person} onChange={set('contact_person')} />
            </Row>
            <div style={{ marginBottom: 13 }}>
              <label style={lbl}>Business Type *</label>
              <select value={f.customer_type} onChange={set('customer_type')} style={inp}>
                <option value="">Select business type…</option>
                {BUSINESS_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <Row>
              <F label="GST / Tax ID (if any)" value={f.gstin} onChange={set('gstin')} />
              <div style={{ flex: 1 }} />
            </Row>
          </Section>

          <Section title="Contact Details">
            <Row>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Mobile *</label>
                <StorePhoneInput value={f.mobile} onChange={v => setF(prev => ({ ...prev, mobile: v }))} required />
              </div>
              <F label="Email *" type="email" value={f.email} onChange={set('email')} />
            </Row>
          </Section>

          <Section title="Business Address">
            <F label="Address" value={f.address} onChange={set('address')} />
            <Row>
              <F label="City" value={f.city} onChange={set('city')} />
              <F label="State" value={f.state} onChange={set('state')} />
            </Row>
            <Row>
              <F label="Postal Code" value={f.postal_code} onChange={set('postal_code')} />
              <div style={{ flex: 1, marginBottom: 13 }}>
                <label style={lbl}>Country</label>
                <select value={f.country_id} onChange={set('country_id')} style={inp}>
                  <option value="">Select country…</option>
                  {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </Row>
          </Section>

          <Section title="About Your Business (optional)">
            <div style={{ marginBottom: 13 }}>
              <label style={lbl}>Tell us a bit about your business — e.g. approximate monthly order volume, how many outlets you run, or how you found us</label>
              <textarea value={f.notes} onChange={set('notes')} rows={3} style={{ ...inp, resize: 'vertical' }} />
            </div>
          </Section>

          <F label="Password *" type="password" value={f.password} onChange={set('password')} placeholder="At least 6 characters" />
          <Captcha onToken={setCaptchaToken} />
          <button type="submit" disabled={busy} className="sf-btn-outline" style={{ width: '100%', padding: 11, border: '1.5px solid var(--sf-accent)', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 6 }}>
            {busy ? 'Creating…' : 'Create account'}
          </button>
        </form>
        <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 20, paddingTop: 16, textAlign: 'center', fontSize: 13, color: '#888' }}>
          Already have an account? <Link to={`${STORE_BASE}/login`} style={{ color: 'var(--sf-accent)', fontWeight: 500 }}>Sign in</Link>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  );
}

function Row({ children }) {
  return <div style={{ display: 'flex', gap: 10 }}>{children}</div>;
}

function F({ label, value, onChange, type = 'text', placeholder }) {
  return (
    <div style={{ flex: 1, marginBottom: 13 }}>
      <label style={lbl}>{label}</label>
      <input type={type} value={value} onChange={onChange} placeholder={placeholder} style={inp} />
    </div>
  );
}

const lbl = { fontSize: 12.5, fontWeight: 500, color: '#555', display: 'block', marginBottom: 5 };
const inp = { width: '100%', padding: '10px 12px', border: '1.5px solid #e5e5e5', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' };
